import { Validators } from '@angular/forms';

import {
    DynamicField,
    DynamicFieldType,
    DynamicFieldTypes,
    DynamicLabel,
    noZeroRequiredValidator,
    InputTypes,
    NumericInputTypes,
    SelectInputTypes,
} from '@mt-ng2/dynamic-form';
import { getMetaItemValue } from '@mt-ng2/common-functions';

import { IExpandableObject } from '../expandable-object';
import { IDonorBasicInfo } from '../interfaces/donor-basic-info';
import { IUnitsOfMeasurement } from '../interfaces/units-of-measurement';
import { IReferralType } from '../interfaces/referral-type';

export interface IDonorBasicInfoDynamicControlsParameters {
    formGroup?: string;
    heights?: IUnitsOfMeasurement[];
    weights?: IUnitsOfMeasurement[];
    referralTypes?: IReferralType[];
}

export class DonorBasicInfoDynamicControls {

    formGroup: string;
    heights: IUnitsOfMeasurement[];
    weights: IUnitsOfMeasurement[];
    referralTypes: IReferralType[];

    Form: IExpandableObject;
    View: IExpandableObject;

    constructor(private donorbasicinfo?: IDonorBasicInfo, additionalParameters?: IDonorBasicInfoDynamicControlsParameters) {
        this.formGroup = additionalParameters && additionalParameters.formGroup || 'DonorBasicInfo';
        this.heights = additionalParameters && additionalParameters.heights || undefined;
        this.weights = additionalParameters && additionalParameters.weights || undefined;
        this.referralTypes = additionalParameters && additionalParameters.referralTypes || undefined;

        this.Form = {
            ApplicantPhoneNumber: new DynamicField({
                formGroup: this.formGroup,
                label: 'Applicant Phone Number',
                name: 'ApplicantPhoneNumber',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
                validation: [Validators.required, Validators.maxLength(20)],
                validators: { 'required': true, 'maxlength': 20 },
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('ApplicantPhoneNumber') && this.donorbasicinfo.ApplicantPhoneNumber !== null ? this.donorbasicinfo.ApplicantPhoneNumber.toString() : '',
            }),
            EmergencyContactName: new DynamicField({
                formGroup: this.formGroup,
                label: 'Emergency Contact',
                name: 'EmergencyContactName',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
                validation: [Validators.maxLength(125)],
                validators: { 'maxlength': 125 },
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('EmergencyContactName') && this.donorbasicinfo.EmergencyContactName !== null ? this.donorbasicinfo.EmergencyContactName.toString() : '',
            }),
            EmergencyContactNumber: new DynamicField({
                formGroup: this.formGroup,
                label: 'Emergency Contact Number',
                name: 'EmergencyContactNumber',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
                validation: [Validators.maxLength(20)],
                validators: { 'maxlength': 20 },
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('EmergencyContactNumber') && this.donorbasicinfo.EmergencyContactNumber !== null ? this.donorbasicinfo.EmergencyContactNumber.toString() : '',
            }),
            Comment: new DynamicField({
                formGroup: this.formGroup,
                label: 'Comment',
                name: 'Comment',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
                validation: [Validators.maxLength(100)],
                validators: { 'maxlength': 100 },
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('Comment') && this.donorbasicinfo.Comment !== null ? this.donorbasicinfo.Comment.toString() : '',
            }),
            DateCreated: new DynamicField({
                formGroup: this.formGroup,
                label: 'Date Created',
                name: 'DateCreated',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: InputTypes.Datepicker,
                    scale: null,
                }),
                validation: [Validators.required],
                validators: { 'required': true },
                value: this.donorbasicinfo && this.donorbasicinfo.DateCreated || null,
            }),
            DateModified: new DynamicField({
                formGroup: this.formGroup,
                label: 'Date Modified',
                name: 'DateModified',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: InputTypes.Datepicker,
                    scale: null,
                }),
                validation: [],
                validators: {},
                value: this.donorbasicinfo && this.donorbasicinfo.DateModified || null,
            }),
            Dob: new DynamicField({
                formGroup: this.formGroup,
                label: 'Dob',
                name: 'Dob',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: InputTypes.Datepicker,
                    scale: null,
                }),
                validation: [Validators.required],
                validators: { 'required': true },
                value: this.donorbasicinfo && this.donorbasicinfo.Dob || null,
            }),
            HasFamilyMedicalHistory: new DynamicField({
                formGroup: this.formGroup,
                label: 'Has Family Medical History',
                name: 'HasFamilyMedicalHistory',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Checkbox,
                    inputType: null,
                    scale: null,
                }),
                validation: [],
                validators: {},
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('HasFamilyMedicalHistory') && this.donorbasicinfo.HasFamilyMedicalHistory !== null ? this.donorbasicinfo.HasFamilyMedicalHistory : false,
            }),
            HasTextCommunication: new DynamicField({
                formGroup: this.formGroup,
                label: 'Has Text Communication',
                name: 'HasTextCommunication',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Checkbox,
                    inputType: null,
                    scale: null,
                }),
                validation: [],
                validators: {},
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('HasTextCommunication') && this.donorbasicinfo.HasTextCommunication !== null ? this.donorbasicinfo.HasTextCommunication : false,
            }),
            OptedOutOfEmails: new DynamicField({
                formGroup: this.formGroup,
                label: 'Donor Has Opted Out of E-mails',
                name: 'OptedOutOfEmails',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Checkbox,
                    inputType: null,
                    scale: null,
                }),
                validation: [],
                validators: {},
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('OptedOutOfEmails') && this.donorbasicinfo.OptedOutOfEmails !== null ? this.donorbasicinfo.OptedOutOfEmails : false,
            }),
            GenderId: new DynamicField({
                formGroup: this.formGroup,
                label: 'Gender',
                name: 'GenderId',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Select,
                    inputType: null,
                    scale: null,
                }),
                validation: [Validators.required],
                validators: { 'required': true },
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('GenderId') && this.donorbasicinfo.GenderId !== null ? this.donorbasicinfo.GenderId.toString() : '',
            }),
            Height: new DynamicField({
                formGroup: this.formGroup,
                label: 'Height',
                name: 'Height',
                options: this.heights,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Select,
                    inputType: null,
                    scale: null,
                }),
                validation: [noZeroRequiredValidator],
                validators: { 'required': true },
                value: this.donorbasicinfo && this.donorbasicinfo.Height || null,
            }),
            HeightUnitId: new DynamicField({
                formGroup: this.formGroup,
                label: 'Height Unit',
                name: 'HeightUnitId',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Numeric,
                    inputType: NumericInputTypes.Integer,
                    scale: null,
                }),
                validation: [Validators.required],
                validators: { 'required': true },
                value: this.donorbasicinfo && this.donorbasicinfo.HeightUnitId || null,
            }),
            PlaceOfBirth: new DynamicField({
                formGroup: this.formGroup,
                label: 'Place Of Birth',
                name: 'PlaceOfBirth',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Select,
                    inputType: SelectInputTypes.Dropdown,
                    scale: null,
                }),
                validation: [Validators.required],
                validators: { 'required': true  },
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('PlaceOfBirth') && this.donorbasicinfo.PlaceOfBirth !== null ? this.donorbasicinfo.PlaceOfBirth.toString() : '',
            }),
            ReferralTypeId: new DynamicField({
                formGroup: this.formGroup,
                label: 'Referral Type',
                name: 'ReferralTypeId',
                options: this.referralTypes,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Select,
                    inputType: null,
                    scale: null,
                }),
                validation: [noZeroRequiredValidator],
                validators: { 'required': true },
                value: this.donorbasicinfo && this.donorbasicinfo.ReferralTypeId || null,
            }),
            Ssn: new DynamicField({
                formGroup: this.formGroup,
                label: 'Ssn',
                name: 'Ssn',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
                validation: [Validators.required, Validators.maxLength(44)],
                validators: { 'required': true, 'maxlength': 44 },
                value: this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('Ssn') && this.donorbasicinfo.Ssn !== null ? this.donorbasicinfo.Ssn.toString() : '',
            }),
            Weight: new DynamicField({
                formGroup: this.formGroup,
                label: 'Weight',
                name: 'Weight',
                options: this.weights,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Select,
                    inputType: null,
                    scale: null,
                }),
                validation: [noZeroRequiredValidator],
                validators: { 'required': true },
                value: this.donorbasicinfo && this.donorbasicinfo.Weight || null,
            }),
            WeightUnitId: new DynamicField({
                formGroup: this.formGroup,
                label: 'Weight Unit',
                name: 'WeightUnitId',
                options: null,
                type: new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Numeric,
                    inputType: NumericInputTypes.Integer,
                    scale: null,
                }),
                validation: [Validators.required],
                validators: { 'required': true },
                value: this.donorbasicinfo && this.donorbasicinfo.WeightUnitId || null,
            }),
        };

        this.View = {
            ApplicantPhoneNumber: new DynamicLabel(
                'Applicant Phone Number',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('ApplicantPhoneNumber') && this.donorbasicinfo.ApplicantPhoneNumber !== null ? this.donorbasicinfo.ApplicantPhoneNumber.toString() : '',
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
            ),
            EmergencyContactName: new DynamicLabel(
                'Emergency Contact',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('EmergencyContactName') && this.donorbasicinfo.EmergencyContactName !== null ? this.donorbasicinfo.EmergencyContactName.toString() : '',
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
            ),
            EmergencyContactNumber: new DynamicLabel(
                'Emergency Contact Number',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('EmergencyContactNumber') && this.donorbasicinfo.EmergencyContactNumber !== null ? this.donorbasicinfo.EmergencyContactNumber.toString() : '',
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
            ),
            Comment: new DynamicLabel(
                'Comment',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('Comment') && this.donorbasicinfo.Comment !== null ? this.donorbasicinfo.Comment.toString() : '',
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
            ),
            DateCreated: new DynamicLabel(
                'Date Created',
                this.donorbasicinfo && this.donorbasicinfo.DateCreated || null,
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: InputTypes.Datepicker,
                    scale: null,
                }),
            ),
            DateModified: new DynamicLabel(
                'Date Modified',
                this.donorbasicinfo && this.donorbasicinfo.DateModified || null,
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: InputTypes.Datepicker,
                    scale: null,
                }),
            ),
            Dob: new DynamicLabel(
                'Dob',
                this.donorbasicinfo && this.donorbasicinfo.Dob || null,
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: InputTypes.Datepicker,
                    scale: null,
                }),
            ),
            GenderId: new DynamicLabel(
                'Gender',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('GenderId') && this.donorbasicinfo.GenderId !== null ? this.donorbasicinfo.GenderId.toString() : '',
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
            ),
            HasFamilyMedicalHistory: new DynamicLabel(
                'Has Family Medical History',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('HasFamilyMedicalHistory') && this.donorbasicinfo.HasFamilyMedicalHistory !== null ? this.donorbasicinfo.HasFamilyMedicalHistory : false,
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Checkbox,
                    inputType: null,
                    scale: null,
                }),
            ),
            HasTextCommunication: new DynamicLabel(
                'Has Text Communication',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('HasTextCommunication') && this.donorbasicinfo.HasTextCommunication !== null ? this.donorbasicinfo.HasTextCommunication : false,
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Checkbox,
                    inputType: null,
                    scale: null,
                }),
            ),
            OptedOutOfEmails: new DynamicLabel(
                'Donor Has Opted Out of E-mails',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('OptedOutOfEmails') && this.donorbasicinfo.OptedOutOfEmails !== null ? this.donorbasicinfo.OptedOutOfEmails : false,
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Checkbox,
                    inputType: null,
                    scale: null,
                }),
            ),
            Height: new DynamicLabel(
                'Height',
                getMetaItemValue(this.heights, this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('Height') && this.donorbasicinfo.Height !== null ? this.donorbasicinfo.Height : null),
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Select,
                    inputType: null,
                    scale: null,
                }),
            ),
            HeightUnitId: new DynamicLabel(
                'Height Unit',
                this.donorbasicinfo && this.donorbasicinfo.HeightUnitId || null,
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Numeric,
                    inputType: NumericInputTypes.Integer,
                    scale: null,
                }),
            ),
            PlaceOfBirth: new DynamicLabel(
                'Place Of Birth',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('PlaceOfBirth') && this.donorbasicinfo.PlaceOfBirth !== null ? this.donorbasicinfo.PlaceOfBirth.toString() : '',
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
            ),
            ReferralTypeId: new DynamicLabel(
                'Referral Type',
                getMetaItemValue(this.referralTypes, this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('ReferralTypeId') && this.donorbasicinfo.ReferralTypeId !== null ? this.donorbasicinfo.ReferralTypeId : null),
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Select,
                    inputType: null,
                    scale: null,
                }),
            ),
            Ssn: new DynamicLabel(
                'Ssn',
                this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('Ssn') && this.donorbasicinfo.Ssn !== null ? this.donorbasicinfo.Ssn.toString() : '',
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Input,
                    inputType: null,
                    scale: null,
                }),
            ),
            Weight: new DynamicLabel(
                'Weight',
                getMetaItemValue(this.weights, this.donorbasicinfo && this.donorbasicinfo.hasOwnProperty('Weight') && this.donorbasicinfo.Weight !== null ? this.donorbasicinfo.Weight : null),
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Select,
                    inputType: null,
                    scale: null,
                }),
            ),
            WeightUnitId: new DynamicLabel(
                'Weight Unit',
                this.donorbasicinfo && this.donorbasicinfo.WeightUnitId || null,
                new DynamicFieldType({
                    fieldType: DynamicFieldTypes.Numeric,
                    inputType: NumericInputTypes.Integer,
                    scale: null,
                }),
            ),
        };

    }
}
